import request from "../../utils/request";
import {
  LeaveRequestView, LeaveBalanceView, LeaveType, LeavesConfigView, LeavesConfigInput,
  LeaveRequestInput, LeaveRequestsView, LeaveRequestApprovalInput, QuotaUnit,
  LeaveAttachmentsInput, LeaveRequestAttachmentView, LeaveTypeView, LeaveTypeInput, BalanceAdjustmentInput,
  UpcomingHolidayView, LeaveRequestFilterParams, PaginatedResult,
  TeamSummaryView, PreviousDecisionView, ConflictView, TeamOverlapView, TeamCalendarView,
  LeavePolicyView, LeavePolicyInput, AccrualRuleView, AccrualRuleInput,
  WorkflowView, WorkflowInput, WorkflowTestInput, WorkflowInstanceView,
  CarryForwardRuleView, CarryForwardRuleInput,
  BlackoutPeriodView, BlackoutPeriodInput, BlackoutCheckResult, BlackoutImpactView,
  HolidayView, HolidayInput, PolicyAuditLogView, PolicyValidationConfig,
  DelegationAssignmentView, DelegationAssignmentInput,
  ReturnToWorkRequestView,
  CompOffCreditView, CompOffCreditInput,
  ShortLeaveRequestView, ShortLeaveRequestInput, ShortLeaveApprovalInput, ShortLeaveUsageView,
  EncashmentRequestView, EncashmentEligibilityView, EncashmentRequestInput, EncashmentApprovalInput,
  BatchEncashmentPreviewView, BatchEncashmentResultView, BatchEncashmentInput,
  RecurringLeavePatternView, RecurringLeavePreviewView, RecurringLeavePatternInput,
  RestrictedHolidayListView, RestrictedHolidayListInput, RestrictedHolidaySelectionInput,
  CompanyStatutoryLeaveTemplateView, CompanyStatutoryLeaveTemplateInput, StatutoryComplianceWarning,
  YearEndPreviewView, YearEndExecutionResultView, YearEndProcessingRunView,
  LeaveStatementSummaryView, GenerateStatementsResultView,
  AbsenteeismAnalyticsView, BradfordFactorReportView,
  EmployeeLeaveContextView,
  AllBalancesView, AllBalancesFilterInput, BulkBalanceAdjustInput, BulkAdjustResultView,
  LeaveConfigCountryView, CompanyLeaveRulesView, CompanyLeaveRulesInput, CompanyLeaveRulesSaveResultView
} from "../../models/attendance";
import { Result } from "../../models/general";

/**
 * A missing user id must never reach the URL. `Leave/GetBalancePerUser/${undefined}` stringifies
 * to `.../undefined`, and the backend's `{userId=}` route segment happily binds that literal —
 * it is neither null nor empty, so the server-side "fall back to the caller's own id"
 * substitution does not fire and the request used to come back as a permission denial. On the
 * web that denial was a 401, which burned a silent token renew and then force-logged the user
 * out (`utils/requestHandling.ts` → forceLogout('retry-after-renew-still-401')).
 *
 * So: never issue the request at all. Callers that legitimately mean "me" pass no id and get the
 * empty segment (the server substitutes the caller); callers that require an explicit id get a
 * rejected promise they can render as an error instead of a logout.
 */
export class MissingUserIdError extends Error {
  constructor(endpoint: string) {
    super(`Refusing to call ${endpoint} without a user id`);
    this.name = 'MissingUserIdError';
  }
}

function isMissingUserId(userId?: string | null): boolean {
  const value = (userId ?? '').trim().toLowerCase();
  return value === '' || value === 'null' || value === 'undefined';
}

/** Optional-id endpoints: collapse a missing/sentinel id to the empty segment ("me"). */
function userIdSegment(userId?: string | null): string {
  return isMissingUserId(userId) ? '' : (userId as string);
}

export async function getLeaveTypes(userId?: string) {
  const response = await request.get<Result<LeaveTypeView[]>>(`Leave/LeaveTypes/${userIdSegment(userId)}`);
  return response.data;
}

export async function getCompanyLeaveTypes() {
  const response = await request.get<Result<LeaveTypeView[]>>('Leave/CompanyLeaveTypes');
  return response.data;
}

/**
 * @deprecated Legacy company-leave-config read. Superseded by `getCompanyLeaveTypeRules`
 * (`Leave/Policies/Company/{companyId}/LeaveTypeRules`). Kept only so the E2E seeding helpers keep
 * working through release N; deleted in N+1 together with the server action.
 * See docs/leave-policies/SPEC-operator-cross-tenant-policies.md §8.
 *
 * 🔴 It is also not a pure read: the server-side implementation `Add()`s missing `CompanyLeave` rows
 * to the tracked context, so an unrelated `SaveChanges` in the same request flushes them. The
 * replacement route is a pure read. Do not reach for this one in new code.
 */
export async function getLeavesConfig(companyId: number, userId?: string) {
  const response = await request.get<Result<LeavesConfigView>>(`Leave/LeavesConfig/${companyId}`,   { params: { userId } } );
  return response.data;
}

/**
 * @deprecated Legacy company-leave-config write. Superseded by `saveCompanyLeaveTypeRules`
 * (company-level rules) and `bulkAdjustBalances` (a named employee's balance). Deleted in release N+1.
 */
export async function updateLeavesConfig(input: LeavesConfigInput) {
  const response = await request.post<Result<LeavesConfigView>>("Leave/UpdateLeavesConfig", input);
  return response.data;
}

/* ─────────── Company leave configuration (cross-tenant: Operator, Admin, Hr) ───────────
 *
 * `Leave/Policies/Company/{companyId}/…` — the `companyId`-addressed home of company-level leave
 * setup, per docs/leave-policies/SPEC-operator-cross-tenant-policies.md §3.2.
 *
 * The route is the ONLY authority for the tenant: no body here carries a `companyId`, and none of
 * these calls takes or returns anything employee-scoped (no user ids, names, balances or requests).
 * A caller who is not Operator gets 404 — not 403 — for a company that is not their own.
 */

/** Route 1 — the country axis. `includeAll` returns every country so a tenant with no employees is
 *  still configurable (owner decision 2026-09-04). */
export async function getCompanyLeaveCountries(companyId: number, includeAll = false) {
  const response = await request.get<Result<LeaveConfigCountryView[]>>(
    `Leave/Policies/Company/${companyId}/Countries`,
    { params: includeAll ? { includeAll: true } : undefined },
  );
  return response.data;
}

/** Route 2 — leave types of the addressed company. Distinct from `getCompanyLeaveTypes()` above,
 *  which reads the CALLER's own company through `Leave/CompanyLeaveTypes`. */
export async function getCompanyConfigLeaveTypes(companyId: number) {
  const response = await request.get<Result<LeaveTypeView[]>>(`Leave/Policies/Company/${companyId}/LeaveTypes`);
  return response.data;
}

/** Route 3 — create or update a leave type. Omitting `id` means create. Richer contract than the
 *  legacy `createLeaveType`, which only honoured `name`. */
export async function saveCompanyLeaveType(companyId: number, input: LeaveTypeInput) {
  const response = await request.post<Result<LeaveTypeView>>(`Leave/Policies/Company/${companyId}/LeaveTypes`, input);
  return response.data;
}

/** Route 4 — the (leave type x country) grid. Pure read. `countryIds` widens the default axis. */
export async function getCompanyLeaveTypeRules(companyId: number, countryIds?: number[]) {
  const response = await request.get<Result<CompanyLeaveRulesView>>(
    `Leave/Policies/Company/${companyId}/LeaveTypeRules`,
    countryIds?.length ? { params: { countryIds: countryIds.join(',') } } : undefined,
  );
  return response.data;
}

/** Route 5 — batch upsert of the grid. Proportional: unchanged rows are skipped server-side. */
export async function saveCompanyLeaveTypeRules(companyId: number, input: CompanyLeaveRulesInput) {
  const response = await request.put<Result<CompanyLeaveRulesSaveResultView>>(
    `Leave/Policies/Company/${companyId}/LeaveTypeRules`,
    input,
  );
  return response.data;
}

export async function getLeaveRequestsPerUser(userId?: string) {
  const response = await request.get<Result<LeaveRequestsView>>(`Leave/${userIdSegment(userId)}`);
  return response.data;
}

export async function getLeaveRequestsAssignedToUser(userId?: string) {
  const response = await request.get<Result<LeaveRequestsView>>(`Leave/LeaveRequestsAssignedToUser/${userIdSegment(userId)}`);
  return response.data;
}

export async function getLeaveTypeQuotaUnit(leaveTypeId: number) {
  const response = await request.get<Result<QuotaUnit>>(`Leave/QuotaUnit/${leaveTypeId}`);
  return response.data;
}


export async function getBalancePerUser(userId: string) {
  if (isMissingUserId(userId)) throw new MissingUserIdError('Leave/GetBalancePerUser');
  const response = await request
    .get<Result<LeaveBalanceView[]>>(`Leave/GetBalancePerUser/${userId}`);
  return response.data;
}

export async function adjustBalance(input: BalanceAdjustmentInput) {
  const response = await request.post<Result<LeaveBalanceView>>('Leave/Balance/Adjust', input);
  return response.data;
}

export async function getAllBalances(filter?: AllBalancesFilterInput) {
  const response = await request.get<Result<AllBalancesView>>('Leave/AllBalances', { params: filter });
  return response.data;
}

export async function bulkAdjustBalances(input: BulkBalanceAdjustInput) {
  const response = await request.post<Result<BulkAdjustResultView>>('Leave/Balance/BulkAdjust', input);
  return response.data;
}

export async function requestLeave(input: LeaveRequestInput) {
  const response = await request.post<Result<LeaveRequestView>>("Leave/RequestLeave", input);
  return response.data;
}

export async function getLeaveRequest(leaveRequestId: number) {
  const response = await request.get<Result<LeaveRequestView>>(`Leave/GetLeaveRequest/${leaveRequestId}`);
  return response.data;
}

export async function leaveRequestApproval(input: LeaveRequestApprovalInput) {
  const response = await request.post<Result<LeaveRequestView>>("Leave/LeaveRequestApproval", input);
  return response.data;
}

export async function leaveRequestUndoApproval(input: LeaveRequestApprovalInput) {
  const response = await request.post<Result<LeaveRequestView>>("Leave/LeaveRequestUndoApproval", input);
  return response.data;
}

/**
 * @deprecated Accepts a raw `LeaveType` entity but the server only honours `name` + `companyId`.
 * Superseded by `saveCompanyLeaveType`. Retained through release N because the E2E seeding helpers
 * still post it; its release-N+1 deletion is gated on a `mowazaf-flutter` grep for mobile callers
 * (SPEC-operator-cross-tenant-policies.md §8, N+1 step 6).
 */
export async function createLeaveType(input: LeaveType) {
  const response = await request.post<Result<LeaveRequestView>>("Leave/CreateLeaveType", input);
  return response.data;
}

export async function addLeaveAttachments(input: LeaveAttachmentsInput) {
  const formData = new FormData();
  formData.append('leaveRequestId', input.leaveRequestId.toString())
  for (let i = 0; i < input.attachments.length; i++)
    formData.append('attachments', input.attachments[i])

  const response = await request.post<Result<LeaveRequestAttachmentView[]>>(`Leave/AddLeaveAttachments`,
    formData);
  return response.data;
}

export async function getLeaveRequestAttachments(leaveRequestId: number) {
  const response = await request
    .get<Result<LeaveRequestAttachmentView[]>>(`Leave/GetLeaveRequestAttachments/${leaveRequestId}`);
  return response.data;
}

export async function previewLeaveAttachment(attachmentId: number) {
  const response = await request.get<Blob>(`Leave/PreviewLeaveAttachment/${attachmentId}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function removeLeaveAttachment(leaveRequestId: number, leaveAttachmentId: number) {
  const response = await request.post<Result<LeaveRequestAttachmentView[]>>(`Leave/RemoveLeaveAttachment/${leaveRequestId}/${leaveAttachmentId}`);
  return response.data;
}

// ---- Employee Self-Service Endpoints ----

export async function getMyLeaves(filter?: LeaveRequestFilterParams) {
  const response = await request.get<Result<PaginatedResult<LeaveRequestView>>>('Leave/MyLeaves', { params: filter });
  return response.data;
}

export async function getMyBalances() {
  const response = await request.get<Result<LeaveBalanceView[]>>('Leave/MyBalances');
  return response.data;
}

export async function cancelLeaveRequest(id: number, reason?: string) {
  const response = await request.post<Result<LeaveRequestView>>(`Leave/Cancel/${id}`, { reason });
  return response.data;
}

export async function processCancellationApproval(leaveRequestId: number, approved: boolean, comments?: string) {
  const response = await request.post<Result<LeaveRequestView>>(`Leave/CancellationApproval/${leaveRequestId}`, { approved, comments });
  return response.data;
}

export async function getWorkflowInstanceByLeaveRequest(leaveRequestId: number) {
  const response = await request.get<Result<WorkflowInstanceView>>(`Leave/Workflows/Instances/ByLeaveRequest/${leaveRequestId}`);
  return response.data;
}

export async function getUpcomingHolidays(days: number = 60) {
  const response = await request.get<Result<UpcomingHolidayView[]>>('Holiday/upcoming', { params: { days } });
  return response.data;
}

// ---- Manager Approval Endpoints ----

export async function getPendingApprovals(filter?: LeaveRequestFilterParams) {
  const response = await request.get<Result<PaginatedResult<LeaveRequestView>>>('Leave/PendingApprovals', { params: filter });
  return response.data;
}

export async function approveLeave(leaveRequestId: number, comments?: string) {
  const response = await request.post<Result<LeaveRequestView>>(`Leave/Approve/${leaveRequestId}`, JSON.stringify(comments || ''), {
    headers: { 'Content-Type': 'application/json' }
  });
  return response.data;
}

export async function rejectLeave(leaveRequestId: number, comments: string) {
  const response = await request.post<Result<LeaveRequestView>>(`Leave/Reject/${leaveRequestId}`, JSON.stringify(comments), {
    headers: { 'Content-Type': 'application/json' }
  });
  return response.data;
}

export async function bulkApproveLeaves(leaveRequestIds: number[], comments?: string) {
  const response = await request.post<Result<number>>('Leave/BulkApprove', { leaveRequestIds, comments });
  return response.data;
}

export async function undoLeaveDecision(leaveRequestId: number) {
  const response = await request.post<Result<LeaveRequestView>>(`Leave/UndoDecision/${leaveRequestId}`);
  return response.data;
}

export async function getTeamSummary() {
  const response = await request.get<Result<TeamSummaryView>>('Leave/TeamSummary');
  return response.data;
}

export async function getTeamCalendar(from: string, to: string, params?: {
  search?: string; departmentId?: number; pageNumber?: number; pageSize?: number;
}) {
  const response = await request.get<Result<TeamCalendarView>>(`Leave/TeamCalendar/${from}/${to}`, { params });
  return response.data;
}

export async function getPreviousDecisions(filter?: LeaveRequestFilterParams) {
  const response = await request.get<Result<PaginatedResult<PreviousDecisionView>>>('Leave/PreviousDecisions', { params: filter });
  return response.data;
}

export async function getLeaveConflicts(leaveRequestId: number) {
  const response = await request.get<Result<ConflictView[]>>(`Leave/Conflicts/${leaveRequestId}`);
  return response.data;
}

export async function getTeamOverlap(leaveRequestId: number) {
  const response = await request.get<Result<TeamOverlapView>>(`Leave/Conflicts/${leaveRequestId}/TeamOverlap`);
  return response.data;
}

export async function getEmployeeLeaveContext(userId: string, leaveTypeId: number) {
  if (isMissingUserId(userId)) throw new MissingUserIdError('Leave/EmployeeContext');
  const response = await request.get<Result<EmployeeLeaveContextView>>(`Leave/EmployeeContext/${userId}`, { params: { leaveTypeId } });
  return response.data;
}

// export async function getCompanyLeaveTypesDictionary(companyId: number) {
//   const response = await request.get<Result<CompanyLeaveType[]>>(`Leave/CompanyLeaveTypes/dictionary/${companyId}`);
//   return response.data;
// }

// export async function assignLeavesToCompany(companyLeaveTypeInput: CompanyLeaveTypeInput) {
//   const response = await request.post<Result<CompanyLeaveTypeInput>>("Leave/AssignLeavesTocompany", companyLeaveTypeInput);
//   return response.data;
// }

// export async function assignLeavesToUser(input:AllowedLeaveInput) {
//   const response = await request.post<Result<AllowedLeave>>("Leave/AssignLeavesTouser", input


//   );
//   return response.data;
// }

// ─── Leave Policy Management API ───

export async function getLeavePolicies() {
  const response = await request.get<Result<LeavePolicyView[]>>('Leave/Policies');
  return response.data;
}

export async function getLeavePolicy(policyId: number) {
  const response = await request.get<Result<LeavePolicyView>>(`Leave/Policies/${policyId}`);
  return response.data;
}

export async function createLeavePolicy(input: LeavePolicyInput) {
  const response = await request.post<Result<LeavePolicyView>>('Leave/Policies', input);
  return response.data;
}

export async function updateLeavePolicy(policyId: number, input: LeavePolicyInput) {
  const response = await request.put<Result<LeavePolicyView>>(`Leave/Policies/${policyId}`, input);
  return response.data;
}

export async function deleteLeavePolicy(policyId: number) {
  const response = await request.delete<Result<boolean>>(`Leave/Policies/${policyId}`);
  return response.data;
}

export async function getPolicyLeaveTypes(policyId: number) {
  const response = await request.get<Result<LeaveTypeView[]>>(`Leave/Policies/${policyId}/LeaveTypes`);
  return response.data;
}

export async function savePolicyLeaveType(policyId: number, input: LeaveTypeInput) {
  const response = await request.post<Result<LeaveTypeView>>(`Leave/Policies/${policyId}/LeaveTypes`, input);
  return response.data;
}

export async function deletePolicyLeaveType(policyId: number, leaveTypeId: number, options?: { forceDelete?: boolean }) {
  const response = await request.delete<Result<boolean>>(
    `Leave/Policies/${policyId}/LeaveTypes/${leaveTypeId}`,
    { params: options?.forceDelete ? { forceDelete: true } : undefined }
  );
  return response.data;
}

export async function getLeaveTypeBalanceCount(policyId: number, leaveTypeId: number) {
  const response = await request.get<Result<number>>(
    `Leave/Policies/${policyId}/LeaveTypes/${leaveTypeId}/BalanceCount`
  );
  return response.data;
}

export async function getAccrualRules(policyId: number) {
  const response = await request.get<Result<AccrualRuleView[]>>(`Leave/Policies/${policyId}/AccrualRules`);
  return response.data;
}

export async function saveAccrualRule(policyId: number, input: AccrualRuleInput) {
  const response = await request.post<Result<AccrualRuleView>>(`Leave/Policies/${policyId}/AccrualRules`, input);
  return response.data;
}

export async function updateAccrualRule(ruleId: number, input: AccrualRuleInput) {
  const response = await request.put<Result<AccrualRuleView>>(`Leave/AccrualRules/${ruleId}`, input);
  return response.data;
}

export async function deleteAccrualRule(policyId: number, ruleId: number) {
  const response = await request.delete<Result<boolean>>(`Leave/AccrualRules/${ruleId}`);
  return response.data;
}

export async function getAccrualRuleEligibleCount(policyId: number, filters?: {
  departmentIds?: string;
  branchIds?: string;
  employmentTypes?: string;
  minYearsOfService?: number;
  maxYearsOfService?: number;
  ftePercentage?: number;
}) {
  const params = new URLSearchParams();
  if (filters?.departmentIds) params.append('departmentIds', filters.departmentIds);
  if (filters?.branchIds) params.append('branchIds', filters.branchIds);
  if (filters?.employmentTypes) params.append('employmentTypes', filters.employmentTypes);
  if (filters?.minYearsOfService) params.append('minYearsOfService', String(filters.minYearsOfService));
  if (filters?.maxYearsOfService) params.append('maxYearsOfService', String(filters.maxYearsOfService));
  if (filters?.ftePercentage !== undefined && filters.ftePercentage !== 100) params.append('ftePercentage', String(filters.ftePercentage));
  const qs = params.toString();
  const response = await request.get<Result<number>>(`Leave/Policies/${policyId}/AccrualRules/EligibleCount${qs ? `?${qs}` : ''}`);
  return response.data;
}

export async function getApprovalWorkflows(policyId: number) {
  const response = await request.get<Result<WorkflowView[]>>(`Leave/Policies/${policyId}/Workflows`);
  return response.data;
}

export async function saveApprovalWorkflow(policyId: number, input: WorkflowInput) {
  const response = await request.post<Result<WorkflowView>>(`Leave/Policies/${policyId}/Workflows`, input);
  return response.data;
}

export async function updateApprovalWorkflow(workflowId: number, input: WorkflowInput) {
  const response = await request.put<Result<WorkflowView>>(`Leave/Workflows/${workflowId}`, input);
  return response.data;
}

export async function deleteApprovalWorkflow(policyId: number, workflowId: number) {
  const response = await request.delete<Result<boolean>>(`Leave/Workflows/${workflowId}`);
  return response.data;
}

export async function testApprovalWorkflow(policyId: number, input: WorkflowTestInput) {
  const response = await request.post<Result<WorkflowView>>(`Leave/Policies/${policyId}/Workflows/Test`, input);
  return response.data;
}

export async function getCarryForwardRules(policyId: number) {
  const response = await request.get<Result<CarryForwardRuleView[]>>(`Leave/Policies/${policyId}/CarryForward`);
  return response.data;
}

export async function saveCarryForwardRule(policyId: number, input: CarryForwardRuleInput) {
  const response = await request.post<Result<CarryForwardRuleView>>(`Leave/Policies/${policyId}/CarryForward`, input);
  return response.data;
}

export async function updateCarryForwardRule(ruleId: number, input: CarryForwardRuleInput) {
  const response = await request.put<Result<CarryForwardRuleView>>(`Leave/CarryForward/${ruleId}`, input);
  return response.data;
}

export async function deleteCarryForwardRule(policyId: number, ruleId: number) {
  const response = await request.delete<Result<boolean>>(`Leave/CarryForward/${ruleId}`);
  return response.data;
}

export async function checkBlackoutPeriods(leaveTypeId: number, startDate: string, endDate: string) {
  const response = await request.get<Result<BlackoutCheckResult>>(`Leave/CheckBlackouts`, {
    params: { leaveTypeId, startDate, endDate }
  });
  return response.data;
}

export async function getValidationConfig(userId?: string) {
  const response = await request.get<Result<PolicyValidationConfig>>(`Leave/ValidationConfig/${userIdSegment(userId)}`);
  return response.data;
}

export async function getBlackoutImpact(policyId: number, blackoutId: number) {
  const response = await request.get<Result<BlackoutImpactView>>(`Leave/Policies/${policyId}/Blackouts/${blackoutId}/Impact`);
  return response.data;
}

export async function getBlackoutDates(policyId: number) {
  const response = await request.get<Result<BlackoutPeriodView[]>>(`Leave/Policies/${policyId}/Blackouts`);
  return response.data;
}

export async function saveBlackoutDate(policyId: number, input: BlackoutPeriodInput) {
  const response = await request.post<Result<BlackoutPeriodView>>(`Leave/Policies/${policyId}/Blackouts`, input);
  return response.data;
}

export async function updateBlackoutDate(blackoutId: number, input: BlackoutPeriodInput) {
  const response = await request.put<Result<BlackoutPeriodView>>(`Leave/Blackouts/${blackoutId}`, input);
  return response.data;
}

export async function deleteBlackoutDate(policyId: number, blackoutId: number) {
  const response = await request.delete<Result<boolean>>(`Leave/Blackouts/${blackoutId}`);
  return response.data;
}

/**
 * Returns ONE ROW PER (holiday name, country) — and, since the server stopped filtering archived
 * rows, disabled rows too. Group by `name` before rendering; see `HolidayView`.
 */
export async function getHolidays() {
  const response = await request.get<Result<HolidayView[]>>('Leave/Holidays');
  return response.data;
}

/**
 * Creates one row per country in `input.countryIds` (or, when that is omitted, per country the
 * company's employees belong to). Resolves to EVERY row created, not a single view.
 */
export async function saveHoliday(input: HolidayInput) {
  const response = await request.post<Result<HolidayView[]>>('Leave/Holidays', input);
  return response.data;
}

/**
 * `applyToAllCountries` (default true) edits every country row of this holiday. Pass `false` to
 * touch only the row identified by `holidayId` — that is the per-country date/status override.
 * The flag binds `[FromQuery]` on `LeaveController.UpdateHoliday`, not from the body.
 */
export async function updateHoliday(holidayId: number, input: HolidayInput, applyToAllCountries = true) {
  const response = await request.put<Result<HolidayView[]>>(
    `Leave/Holidays/${holidayId}?applyToAllCountries=${applyToAllCountries}`,
    input
  );
  return response.data;
}

/** `allCountries` (default true) removes every country row of this holiday. */
export async function deleteHoliday(holidayId: number, allCountries = true) {
  const response = await request.delete<Result<boolean>>(
    `Leave/Holidays/${holidayId}?allCountries=${allCountries}`
  );
  return response.data;
}

export async function importHolidaysFile(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  const response = await request.post<Result<HolidayView[]>>('Leave/Holidays/Import', formData);
  return response.data;
}

export async function getPolicyAudit(policyId: number) {
  const response = await request.get<Result<PolicyAuditLogView[]>>(`Leave/Policies/${policyId}/Audit`);
  return response.data;
}

export async function exportPolicy(policyId: number) {
  const response = await request.post<Result<string>>(`Leave/Policies/Export/${policyId}`);
  return response.data;
}

export async function importPolicy(settingsJson: string) {
  const response = await request.post<Result<LeavePolicyView>>('Leave/Policies/Import', JSON.stringify(settingsJson), {
    headers: { 'Content-Type': 'application/json' }
  });
  return response.data;
}

// Delegation
export async function createDelegation(input: DelegationAssignmentInput) {
  const response = await request.post<Result<DelegationAssignmentView>>('Leave/Delegations', input);
  return response.data;
}

export async function getMyDelegations() {
  const response = await request.get<Result<DelegationAssignmentView[]>>('Leave/Delegations/Mine');
  return response.data;
}

export async function getDelegationsToMe() {
  const response = await request.get<Result<DelegationAssignmentView[]>>('Leave/Delegations/ToMe');
  return response.data;
}

export async function revokeDelegation(delegationId: number) {
  const response = await request.post<Result<boolean>>(`Leave/Delegations/${delegationId}/Revoke`);
  return response.data;
}

// ---- Return-to-Work (Story 5.4) ----

export async function getReturnToWorkRequests() {
  const response = await request.get<Result<ReturnToWorkRequestView[]>>('Leave/ReturnToWork');
  return response.data;
}

export async function getMyReturnToWorkRequests() {
  const response = await request.get<Result<ReturnToWorkRequestView[]>>('Leave/ReturnToWork/My');
  return response.data;
}

export async function updateReturnToWorkNotes(rtwId: number, notes: string) {
  const response = await request.post<Result<ReturnToWorkRequestView>>(`Leave/ReturnToWork/${rtwId}/Notes`, { notes });
  return response.data;
}

export async function signOffReturnToWork(rtwId: number, notes?: string) {
  const response = await request.post<Result<ReturnToWorkRequestView>>(`Leave/ReturnToWork/${rtwId}/SignOff`, { notes: notes || '' });
  return response.data;
}

export async function waiveReturnToWork(rtwId: number, notes?: string) {
  const response = await request.post<Result<ReturnToWorkRequestView>>(`Leave/ReturnToWork/${rtwId}/Waive`, { notes: notes || '' });
  return response.data;
}

// CompOff (Story 6.1)
export async function certifyCompOff(input: CompOffCreditInput) {
  const response = await request.post<Result<CompOffCreditView>>('Leave/CompOff/Certify', input);
  return response.data;
}

export async function getTeamCompOffCredits() {
  const response = await request.get<Result<CompOffCreditView[]>>('Leave/CompOff/Team');
  return response.data;
}

export async function getMyCompOffCredits() {
  const response = await request.get<Result<CompOffCreditView[]>>('Leave/CompOff/My');
  return response.data;
}

export async function cancelCompOffCredit(creditId: number) {
  const response = await request.post<Result<boolean>>(`Leave/CompOff/${creditId}/Cancel`);
  return response.data;
}

// Short Leave
export async function requestShortLeave(input: ShortLeaveRequestInput) {
  const response = await request.post<Result<ShortLeaveRequestView>>('Leave/ShortLeave/Request', input);
  return response.data;
}

export async function getMyShortLeaves() {
  const response = await request.get<Result<ShortLeaveRequestView[]>>('Leave/ShortLeave/My');
  return response.data;
}

export async function getPendingShortLeaveApprovals() {
  const response = await request.get<Result<ShortLeaveRequestView[]>>('Leave/ShortLeave/Pending');
  return response.data;
}

export async function approveShortLeave(requestId: number, input: ShortLeaveApprovalInput) {
  const response = await request.post<Result<ShortLeaveRequestView>>(`Leave/ShortLeave/${requestId}/Approve`, input);
  return response.data;
}

export async function rejectShortLeave(requestId: number, input: ShortLeaveApprovalInput) {
  const response = await request.post<Result<ShortLeaveRequestView>>(`Leave/ShortLeave/${requestId}/Reject`, input);
  return response.data;
}

export async function cancelShortLeave(requestId: number) {
  const response = await request.post<Result<boolean>>(`Leave/ShortLeave/${requestId}/Cancel`);
  return response.data;
}

export async function getShortLeaveMonthlyUsage() {
  const response = await request.get<Result<ShortLeaveUsageView>>('Leave/ShortLeave/Usage');
  return response.data;
}

// Encashment (Story 6.3)
export async function requestEncashment(input: EncashmentRequestInput) {
  const response = await request.post<Result<EncashmentRequestView>>('Leave/Encashment/Request', input);
  return response.data;
}

export async function getMyEncashments() {
  const response = await request.get<Result<EncashmentRequestView[]>>('Leave/Encashment/My');
  return response.data;
}

export async function getPendingEncashments() {
  const response = await request.get<Result<EncashmentRequestView[]>>('Leave/Encashment/Pending');
  return response.data;
}

export async function approveEncashment(requestId: number, input: EncashmentApprovalInput) {
  const response = await request.post<Result<EncashmentRequestView>>(`Leave/Encashment/${requestId}/Approve`, input);
  return response.data;
}

export async function rejectEncashment(requestId: number, input: EncashmentApprovalInput) {
  const response = await request.post<Result<EncashmentRequestView>>(`Leave/Encashment/${requestId}/Reject`, input);
  return response.data;
}

export async function cancelEncashment(requestId: number) {
  const response = await request.post<Result<boolean>>(`Leave/Encashment/${requestId}/Cancel`);
  return response.data;
}

export async function getEncashmentEligibility() {
  const response = await request.get<Result<EncashmentEligibilityView[]>>('Leave/Encashment/Eligibility');
  return response.data;
}

// Batch Encashment (Story 6.4)
export async function getBatchEncashmentPreview(leaveTypeId: number) {
  const response = await request.get<Result<BatchEncashmentPreviewView[]>>(`Leave/Encashment/Batch/Preview/${leaveTypeId}`);
  return response.data;
}

export async function processBatchEncashment(input: BatchEncashmentInput) {
  const response = await request.post<Result<BatchEncashmentResultView>>('Leave/Encashment/Batch/Process', input);
  return response.data;
}

// Recurring Leave (Story 7.1)
export async function previewRecurringLeave(input: RecurringLeavePatternInput) {
  const response = await request.post<Result<RecurringLeavePreviewView>>('Leave/RecurringLeave/Preview', input);
  return response.data;
}

export async function submitRecurringLeave(input: RecurringLeavePatternInput) {
  const response = await request.post<Result<RecurringLeavePatternView>>('Leave/RecurringLeave/Submit', input);
  return response.data;
}

export async function getMyRecurringPatterns() {
  const response = await request.get<Result<RecurringLeavePatternView[]>>('Leave/RecurringLeave/MyPatterns');
  return response.data;
}

export async function cancelRecurringPattern(patternId: number) {
  const response = await request.post<Result<boolean>>(`Leave/RecurringLeave/Cancel/${patternId}`);
  return response.data;
}

// Restricted Holiday Lists (Story 7.2)
export async function createRestrictedHolidayList(input: RestrictedHolidayListInput) {
  const response = await request.post<Result<RestrictedHolidayListView>>('Leave/RestrictedHoliday/Create', input);
  return response.data;
}

export async function getRestrictedHolidayLists() {
  const response = await request.get<Result<RestrictedHolidayListView[]>>('Leave/RestrictedHoliday/Lists');
  return response.data;
}

export async function getRestrictedHolidayList(listId: number) {
  const response = await request.get<Result<RestrictedHolidayListView>>(`Leave/RestrictedHoliday/${listId}`);
  return response.data;
}

export async function openRestrictedHolidayList(listId: number) {
  const response = await request.post<Result<boolean>>(`Leave/RestrictedHoliday/${listId}/Open`);
  return response.data;
}

export async function closeRestrictedHolidayList(listId: number) {
  const response = await request.post<Result<boolean>>(`Leave/RestrictedHoliday/${listId}/Close`);
  return response.data;
}

export async function selectRestrictedHoliday(listId: number, input: RestrictedHolidaySelectionInput) {
  const response = await request.post<Result<boolean>>(`Leave/RestrictedHoliday/${listId}/Select`, input);
  return response.data;
}

export async function deselectRestrictedHoliday(listId: number, optionId: number) {
  const response = await request.post<Result<boolean>>(`Leave/RestrictedHoliday/${listId}/Deselect/${optionId}`);
  return response.data;
}

// Statutory Leave Templates: the caller's company copies (owner Q1 a, 2026-10-15). Every route is HrOnly
// on the server. There is no tenant create (owner Q5 a): POST api/Leave/StatutoryTemplate always answers 403.
export async function getAllStatutoryTemplates(includeArchived = false) {
  const response = await request.get<Result<CompanyStatutoryLeaveTemplateView[]>>(
    `Leave/StatutoryTemplate/All${includeArchived ? '?includeArchived=true' : ''}`);
  return response.data;
}

export async function getStatutoryTemplatesByCountry(countryId: number) {
  const response = await request.get<Result<CompanyStatutoryLeaveTemplateView[]>>(`Leave/StatutoryTemplate/Country/${countryId}`);
  return response.data;
}

/**
 * The PUT replaces every field, the link included: a null leaveTypeId UNLINKS the copy. Callers build the
 * input from the copy (toCompanyStatutoryInput) so an edit of the values never drops the link.
 */
export async function updateStatutoryTemplate(templateId: number, input: CompanyStatutoryLeaveTemplateInput) {
  const response = await request.put<Result<CompanyStatutoryLeaveTemplateView>>(`Leave/StatutoryTemplate/${templateId}`, input);
  return response.data;
}

/** Archives the company copy (DELETE). It can be restored. */
export async function archiveStatutoryTemplate(templateId: number) {
  const response = await request.delete<Result<boolean>>(`Leave/StatutoryTemplate/${templateId}`);
  return response.data;
}

export async function restoreStatutoryTemplate(templateId: number) {
  const response = await request.post<Result<CompanyStatutoryLeaveTemplateView>>(`Leave/StatutoryTemplate/${templateId}/Restore`);
  return response.data;
}

/** Copies the platform values back and clears isCustomized. 400 when the catalogue row is archived. */
export async function resetStatutoryTemplateToPlatform(templateId: number) {
  const response = await request.post<Result<CompanyStatutoryLeaveTemplateView>>(`Leave/StatutoryTemplate/${templateId}/ResetToPlatform`);
  return response.data;
}

/** The PUT body for a copy, with its current link kept. */
export function toCompanyStatutoryInput(copy: CompanyStatutoryLeaveTemplateView): CompanyStatutoryLeaveTemplateInput {
  return {
    leaveTypeName: copy.leaveTypeName,
    minDaysPerYear: copy.minDaysPerYear,
    carryForwardAllowed: copy.carryForwardAllowed,
    maxCarryForwardDays: copy.maxCarryForwardDays,
    probationMonths: copy.probationMonths,
    notes: copy.notes,
    leaveTypeId: copy.leaveTypeId,
  };
}

export async function validateStatutoryCompliance(policyId: number) {
  const response = await request.get<Result<StatutoryComplianceWarning[]>>(`Leave/StatutoryTemplate/ValidatePolicy/${policyId}`);
  return response.data;
}

// Year-End Processing (Story 8.1)
export async function getYearEndPreview(year: number) {
  const response = await request.get<Result<YearEndPreviewView>>(`Leave/YearEnd/Preview/${year}`);
  return response.data;
}

export async function executeYearEnd(year: number) {
  const response = await request.post<Result<YearEndExecutionResultView>>(`Leave/YearEnd/Execute/${year}`);
  return response.data;
}

export async function getYearEndHistory() {
  const response = await request.get<Result<YearEndProcessingRunView[]>>('Leave/YearEnd/History');
  return response.data;
}

// Leave Statements (Story 8.2)
export async function generateStatements(year: number) {
  const response = await request.post<Result<GenerateStatementsResultView>>(`Leave/Statement/Generate/${year}`);
  return response.data;
}

export async function getAllStatements(year: number) {
  const response = await request.get<Result<LeaveStatementSummaryView[]>>(`Leave/Statement/All/${year}`);
  return response.data;
}

export async function getMyStatement(year: number) {
  const response = await request.get<Result<LeaveStatementSummaryView>>(`Leave/Statement/My/${year}`);
  return response.data;
}

// Absenteeism Analytics (Story 8.3)
export async function getAbsenteeismAnalytics(year: number) {
  const response = await request.get<Result<AbsenteeismAnalyticsView>>(`Leave/Analytics/Absenteeism/${year}`);
  return response.data;
}

// Bradford Factor Report (Story 8.4)
export async function getBradfordFactorReport(year: number) {
  const response = await request.get<Result<BradfordFactorReportView>>(`Leave/Analytics/BradfordFactor/${year}`);
  return response.data;
}